<template>
	<header-block title="Activity"></header-block>
	<v-skeleton-loader v-if="!bankInfo" type="image" class="activity-skeleton-item" />

	<transition>
		<div v-if="bankInfo && bankInfo.accounts.length == 0">
			<v-row class="financialFitnessRow">
				<v-col>
					<AdButton class="linkAccounts" title="Link Your Bank Accounts" href="/#/money?add=true"
						subtitle="Money Guided is designed to work with insights into your personal finances. Link your bank accounts to benefit from the power shown in the examples below!"
						:topicon="require('@/assets/coins.png')" :bottomrighticon="require('@/assets/go.png')" />
				</v-col>
			</v-row>

			<v-row class="mt-2" style="opacity: 0.7; filter: blur(1px);">
				<v-col>
					<AnalysisWidget 
						titleSize="small" 
						:fakeTotals="true" 
						:formatter="formatter" 
						:title="true"
						titleLabel="Spending Analysis"  />
				</v-col>
			</v-row>

			<v-row class="mt-2" style="opacity: 0.7; filter: blur(1px);">
				<v-col>
					<InfoPanel :icon="require('@/assets/coins2.png')" title="Tag Transactions"
						subtitle="You have transactions to tag in your current pay cycle" valueTitle="Count"
						:value="7" />
				</v-col>
			</v-row>

			<v-row class="mt-2" style="opacity: 0.7; filter: blur(1px);">
				<v-col>
					<InfoPanel :icon="require('@/assets/coins.png')" title="Recurring Payments"
						subtitle="Stay on top of your recurring payments" valueTitle="Count" :value="13" />
				</v-col>
			</v-row>

			<v-row style="opacity: 0.7; filter: blur(1px);">
				<v-col>
					<PlanInfoPanel :plan="400" :spent="234" title="Groceries Budget"
						:formatter="formatter" />
				</v-col>
			</v-row>
		
			<v-row style="opacity: 0.7; filter: blur(1px);">
				<v-col>
					<GoalInfoPanel :goal="300"
						:saved="80" title="Sarah's New Bike" :formatter="formatter" />
				</v-col>
			</v-row>

		</div>
	</transition>


	<v-skeleton-loader v-if="!bankInfo" type="list-item-avatar-two-line" class="activity-skeleton-item" />

	<transition>
		<v-row class="mt-2"
			v-if="bankInfo && bankInfo.accounts.length > 0 && bankInfo.untaggedTransactionsCurrentCycleCount > 0">
			<v-col>
				<InfoPanel
					:href="'/#/Transaction/Bulk?from=' + bankInfo.currentCycleStartDate + '&to=' + bankInfo.today + '&includeNegative=true&includePositive=true'"
					:valueHref="'/#/Transaction/Bulk?from=' + bankInfo.currentCycleStartDate + '&to=' + bankInfo.today + '&includeNegative=true&includePositive=true'"
					:icon="require('@/assets/coins2.png')" title="Tag Transactions"
					subtitle="You have transactions to tag in your current pay cycle" valueTitle="Count"
					:value="bankInfo.untaggedTransactionsCurrentCycleCount" />
			</v-col>
		</v-row>
	</transition>

	<v-skeleton-loader v-if="bankInfo && bankInfo.accounts.length > 0 && !spendingAnalysisStructured" type="list-item-avatar-two-line" class="activity-skeleton-item" />

	<transition>
		<v-row v-if="bankInfo && bankInfo.accounts.length > 0 && spendingAnalysisStructured" class="mt-2">
			<v-col>
				<AnalysisWidget titleSize="small" 
					:spendingAnalysisStructured="spendingAnalysisStructured"
					:selectedSlotKey="selectedSlotKey"
					:formatter="formatter"
					:title="true"
					titleLabel="Spending Analysis"
					@click="navigateToSpendingAnalysis()" />
			</v-col>
		</v-row>
	</transition>
	
	<v-skeleton-loader v-if="bankInfo && bankInfo.accounts.length > 0 && !planReady" type="list-item-avatar-two-line" class="activity-skeleton-item" />

	<transition>
		<v-row v-if="bankInfo && bankInfo.accounts.length > 0 && spendingPlans && planReady">
			<v-col>
				<PlanInfoPanel @click="navigateToPlan()" :plan=totalPlan :spent=totalSpend title="Spending plan"
					:formatter="formatter" />
			</v-col>
		</v-row>
	</transition>


	<v-skeleton-loader v-if="!recurringPayments" type="list-item-avatar-two-line" class="activity-skeleton-item" />

	<transition>
		<v-row class="mt-2" v-if="recurringPayments && recurringPayments.payments.length > 0">
			<v-col>
				<InfoPanel :href="'/#/RecurringPayments'" :valueHref="'/#/RecurringPayments'"
					:icon="require('@/assets/coins.png')" title="Recurring Payments"
					subtitle="Stay on top of your recurring payments" valueTitle="Count"
					:value="recurringPayments.payments.length" />
			</v-col>
		</v-row>
	</transition>

	<v-skeleton-loader v-if="!customerTags" type="list-item-avatar-two-line" class="activity-skeleton-item" />

	<transition>
		<v-row class="mt-2" v-if="customerTags && customerTags.length > 0">
			<v-col>
				<InfoPanel href="/#/CustomerTagSpend" valueHref="/#/CustomerTagSpend"
					:icon="require('@/assets/Frame.png')" title="Custom #Tags"
					subtitle="Your spending, categorised your way" valueTitle="Count"
					:value="customerTags.length" />
			</v-col>
		</v-row>
	</transition>

	<v-skeleton-loader v-if="bankInfo && bankInfo.accounts.length > 0 && !goalsReady" type="list-item-avatar-two-line" class="activity-skeleton-item" />

	<transition>
		<v-row v-if="bankInfo && bankInfo.accounts.length > 0 && goalsReady" class="mt-2">
			<v-col v-if="totalGoals > 0">
				<GoalInfoPanel @click="navigateToGoals()" :goal="this.totalSavingGoals"
				:saved="this.totalSavingGoalsSaved" :title="totalGoals + ' Savings Goal' + (totalGoals > 1 ? 's' : '')" :formatter="formatter" />
			</v-col>
			<v-col v-if="totalGoals	== 0">
				<InfoPanel :href="'/#/Goals'" :valueHref="'/#/Goals'"
					:icon="require('@/assets/coins2.png')" title="Saving Goals"
					subtitle="Save for the important things in life"
					/>
			</v-col>
		</v-row>
	</transition>
	
	<div style="clear:both; height: 60px;"></div>
	<DrawerPanel ref="transactionsDrawer" :bottom="_peekingDrawerBottom()" title="Recent Transactions" backgroundColor="white" openBackgroundColor="white">
		<TransactionsList @refreshTransaction="(t) => { refreshTransaction(t); }" @cleared="this.transactions = bankInfo.recentTransactions" @searchBack="(query) => { searchBackTransactions(query); }" :allowSearchBack="true" :showDateTotal="true" v-if="transactions" search="true" :transactions="transactions" :formatter="formatter" :today="today" :yesterday="yesterday" />
	</DrawerPanel>


</template>

<script>
import Services from '../mixins/Services';
import AnalysisWidget from './elements/AnalysisWidget.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import Helpers from '../mixins/Helpers';
import PlanInfoPanel from './elements/PlanInfoPanel.vue';
import InfoPanel from './elements/InfoPanel.vue';
import GoalInfoPanel from './elements/GoalInfoPanel.vue';
import AdButton from './elements/AdButton.vue';
import TransactionsList from './elements/TransactionsList.vue';
import DrawerPanel from './elements/DrawerPanel.vue';

export default {
	mixins: [Services, Helpers],
	components: { HeaderBlock, AnalysisWidget, PlanInfoPanel,
		InfoPanel, GoalInfoPanel, AdButton, TransactionsList, DrawerPanel },
	data() {
		return {
			selectedTotals: null,
			goalsReady: false,
			totalSavingGoals: null,
			totalSavingGoalsSaved: null,
			totalGoals: null,
			recurringPayments: null,
			totalPlan: 0,
			totalSpend: 0,
			planReady: false,
			spendingAnalysisAttempts: 0,
			customerTags: null,
			transactions: null
		}
	},
	computed: {
		transactionAccounts() {
			var ret = [];
			if (this.bankInfo == null) {
				return ret;
			}
			this.bankInfo.accounts.forEach(a => {
				if (a.accountType.prettyName.toLowerCase() == "current") {
					ret.push(a);
				}
			});
			return ret;
		},
	},
	methods: {

		waitForSpendingAnalysis : function(onSuccess = null) {
			this._getSpendingAnalysisStructured(() => {
				if (this.spendingAnalysisStructured.totalIn == 0 && this.spendingAnalysisStructured.totalOut == 0 && this.spendingAnalysisStructured.totalNet == 0 && this.spendingAnalysisAttempts < 6) {
					// Wait for analysis to be ready
					this._clearSpendingAnalysis();
					this.spendingAnalysisWaitMessage = "Crunching the numbers...";
					this.spendingAnalysisAttempts ++;
					setTimeout(() => {
						this.waitForSpendingAnalysis(onSuccess);
					}, 5000);
				}
				else {
					this._getSpendingPlansLite(() => {
						this.getTotals();
					});
				}
				if (onSuccess) {
					onSuccess();
				}
			});
		},

		searchBackTransactions: function(query){
			this._searchTransactions(query, (result) => {
				this.transactions = result;
			});
		},

		refreshTransaction: function(transactionId) {
			this._getTransaction(transactionId, (transaction) => {
				if (this.transactions) {
					this.transactions.forEach(group => {
						const index = group.findIndex(t => t.id === transaction.id);
						if (index !== -1) {
							group.splice(index, 1, transaction);
						}
					});
				}
			});
		},

		onLoad: async function () {
			this._getAppConfig();
			this._getCustomer(() => {
				this._getBankingInfo(() => {
					this.transactions = this.bankInfo.recentTransactions;
					if (this.bankInfo.spendingCycle.id == "00000000-0000-0000-0000-000000000000") {
						this.$router.push('/setupspendinganalysis');
						return;
					}
					this.waitForSpendingAnalysis(() => {
						this.slots = this.spendingAnalysisStructured.slotNames;
						this.selectedSlotKey = this.slots ? this.slots[this.slots.length - 2].key : '';
					});
				});
				this._getRecurringPayments((res) => {
					this.recurringPayments = res;
				});
				this._getCustomerTags((tags) => { this.customerTags = tags; });
				this._getSavingGoals(() => {
					this.goalsReady = true;
					this.createGoalsSummary();
				});
			});
		},
		getTotals() {
			this.spendingPlans.plans.forEach(plan => {
				var spend = this.spendingAnalysisStructured.grandparentAnalysis
					.flatMap(gp => gp.groupedAnalysis)
					.find(x => x.transactionClassification.id == plan.classification.id);
				if (spend) {
					plan.spend = spend.amountNet;
					}
			}, false);
			this.totalSpend = Math.round(Math.abs(this.spendingPlans.plans.reduce((a, b) => a + (b.spend || 0), 0)));
			this.totalPlan = Math.round(Math.abs(this.spendingPlans.plans.reduce((a, b) => a + b.plan.amount, 0)));
			this.planReady = true;
		},
		createGoalsSummary() {
			let totalTargetAmount = 0;
			if (this.savingGoals && this.savingGoals.length > 0) {
				this.savingGoals.forEach(goal => {
					totalTargetAmount += goal.targetAmount;
				});
			}
			this.totalSavingGoals = totalTargetAmount;

			let totalCurrentBalance = 0;
			if (this.savingGoals && this.savingGoals.length > 0) {
				this.savingGoals.forEach(goal => {
					totalCurrentBalance += goal.currentBalance;
				});
			}
			this.totalSavingGoalsSaved = totalCurrentBalance;

			this.totalGoals = this.savingGoals.length;
		},
		navigateToSpendingAnalysis() {
			this.$router.push('/SpendingAnalysis');
			//this._openV1('/Moves/Spending', 'Activity');
		},
		navigateToPlan() {
			this.$router.push('/SpendingPlan');
			//this._openV1('/Moves/Spending/Plan', 'Activity');
		},
		navigateToGoals() {
			this.$router.push('/Goals');
			//this._openV1('appscreen/Goals', 'Activity');
		}

	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>
.activity-skeleton-item {
	border-radius: 8px;
	margin-bottom: 8px;
}

.linkAccounts {
	padding-top: 10px;
}

.moves-title-row {
	margin-top: 10px;
	padding-top: 10px;

	.v-col {
		padding-bottom: 5px;

		a {
			text-decoration: underline;
		}
	}
}

.moves-widget-col {
	padding-top: 0px;
	height: 275px;
}
</style>
